import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  LabelList,
  Cell,
} from 'recharts';
import ChartCard, { FocalBadge, focalTickFactory, yearTickFactory } from './ChartCard';
import ChartTooltip from './ChartTooltip';
import { tooltipNotesForYear } from './chartNotes';
import MarketDataFootnote from './MarketDataFootnote';
import { formatFiscalYear } from '../utils/formatters';
import { PEER_BAR_UNIFORM, BAR_MAX_SIZE, METRIC_COLOR, gridProps, xAxisProps, yAxisProps, legendProps, zeroLineProps, refLineProps, CHART_INK, CHART_TYPE } from './chartTheme';
import type { UnifiedFinancialRecord, RegionConfig, DataNote } from '../../../types/financial';

interface Props {
  data: UnifiedFinancialRecord[];
  region?: RegionConfig;
  companyName?: string;
  competitorData?: Record<string, UnifiedFinancialRecord[]>;
}

function getLatestWacc(records: UnifiedFinancialRecord[]): number | null {
  const sorted = [...records].sort((a, b) => b.year - a.year);
  for (const r of sorted) {
    if (r.wacc != null) return r.wacc * 100;
  }
  return null;
}

export default function WACCTrendChart({ data, region, companyName, competitorData }: Props) {
  const isPeerMode =
    !!competitorData && Object.keys(competitorData).length > 0 && !!companyName;

  if (isPeerMode) {
    const focalWacc = getLatestWacc(data);
    if (focalWacc == null) return null;

    const peerRows = [
      { company: companyName!, wacc: parseFloat(focalWacc.toFixed(2)), isFocal: true },
      ...Object.entries(competitorData!)
        .map(([name, records]) => {
          const w = getLatestWacc(records);
          return w != null
            ? { company: name, wacc: parseFloat(w.toFixed(2)), isFocal: false }
            : null;
        })
        .filter((r): r is { company: string; wacc: number; isFocal: boolean } => r != null),
    ].sort((a, b) => a.wacc - b.wacc); // Lowest WACC (cheapest capital) first

    if (peerRows.length < 2) return null;

    const avg = peerRows.reduce((s, r) => s + r.wacc, 0) / peerRows.length;

    return (
      <ChartCard
        title="WACC — Peer Comparison (%)"
        subtitle="Weighted Average Cost of Capital, sorted ascending (cheaper capital first)."
        action={<FocalBadge name={companyName!} />}
      >
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={peerRows} margin={{ top: 24, right: 16, left: 8, bottom: 60 }}>
            <CartesianGrid {...gridProps} />
            <XAxis dataKey="company" interval={0} height={70} tick={focalTickFactory(companyName!)} />
            <YAxis
              {...yAxisProps}
              width={56}
              tickFormatter={(v) => `${v.toFixed(1)}%`}
              label={{ value: 'WACC (%)', angle: -90, position: 'insideLeft', style: { fontSize: CHART_TYPE.axisTick, fill: CHART_INK.muted } }}
            />
            <ReferenceLine
              y={avg}
              {...refLineProps}
              label={{ value: `Avg ${avg.toFixed(1)}%`, position: 'right', fill: CHART_INK.muted, fontSize: CHART_TYPE.axisTick }}
            />
            <Tooltip content={<ChartTooltip format={(v) => `${v.toFixed(2)}%`} />} />
            {/* One uniform tone for every company; focal marked by the ★ tick. */}
            <Bar dataKey="wacc" name="WACC (%)" radius={[4, 4, 0, 0]} maxBarSize={BAR_MAX_SIZE}>
              <LabelList
                dataKey="wacc"
                position="top"
                formatter={(v: number) => `${v?.toFixed(1)}%`}
                style={{ fontSize: CHART_TYPE.dataLabel, fill: CHART_INK.label, fontWeight: 600 }}
              />
              {peerRows.map((row, i) => (
                <Cell key={i} fill={PEER_BAR_UNIFORM} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>
    );
  }

  const chartData = data.map((d) => {
    const hasWacc = d.wacc != null;
    return {
      year: formatFiscalYear(d.year, region),
      wacc: hasWacc ? d.wacc! * 100 : null,
      roic: d.roic != null ? d.roic * 100 : null,
      // Explain a WACC gap on hover: the year's own note (e.g.
      // MARKET_CAP_UNDERIVABLE — "no market price for this year") when present,
      // else the generic missing-market-data fallback. `hasWacc` is the gap
      // signal here because WACC is the only series that goes missing; ROIC is
      // an operating metric present for every year.
      notes: tooltipNotesForYear(d.data_notes, hasWacc),
    };
  });

  const notesByYear = new Map<string, DataNote[]>(chartData.map((d) => [d.year, d.notes]));

  const latestWaccValue = data.at(-1)?.wacc;
  const latestRoic = data.at(-1)?.roic;
  const spread = latestRoic != null && latestWaccValue != null ? ((latestRoic - latestWaccValue) * 100).toFixed(1) : null;

  const spreadBadge = spread ? (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${parseFloat(spread) > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      Spread: {spread}%
    </span>
  ) : undefined;

  return (
    <ChartCard
      title="WACC vs ROIC Trend (%)"
      action={spreadBadge}
      footnote={
        <MarketDataFootnote
          records={data}
          region={region}
          hasMetric={(r) => r.wacc != null}
          metricLabel="WACC"
        />
      }
    >
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={chartData} margin={{ top: 20, right: 16, left: 8, bottom: 0 }}>
          <CartesianGrid {...gridProps} />
          <XAxis dataKey="year" {...xAxisProps} tick={yearTickFactory(notesByYear)} />
          <YAxis {...yAxisProps} tickFormatter={(v) => `${v?.toFixed(1)}%`} />
          <Tooltip content={<ChartTooltip notesKey="notes" format={(v) => `${v.toFixed(1)}%`} />} />
          <Legend {...legendProps} />
          <ReferenceLine y={0} {...zeroLineProps} />
          <Line type="monotone" dataKey="wacc" name="WACC" stroke={METRIC_COLOR.wacc} dot={{ r: 3 }} strokeWidth={2} />
          <Line type="monotone" dataKey="roic" name="ROIC" stroke={METRIC_COLOR.roic} dot={{ r: 3 }} strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
